import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, ArrowLeft, Play, UserCheck, UserPlus, Users, Sparkles } from 'lucide-react';
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import API from "../config";

export default function LocalSetup() {
    const navigate = useNavigate();
    const [roster, setRoster] = useState([]);
    const [selectedPlayers, setSelectedPlayers] = useState([]);
    const [newName, setNewName] = useState("");
    const [loading, setLoading] = useState(false);
    const [hostId, setHostId] = useState("");
    const [startingAssistance, setStartingAssistance] = useState(true);
    const [overrideRoles, setOverrideRoles] = useState(false);
    const [customInfiltrators, setCustomInfiltrators] = useState(1);
    const [customImpostor, setCustomImpostor] = useState(1);
    const [directorMode, setDirectorMode] = useState(false);
    const [enableScoring, setEnableScoring] = useState(true);

    useEffect(() => {
        // Safe helper for UUID generation
        const generateUUID = () => {
            if (typeof crypto !== 'undefined' && crypto.randomUUID) {
                return crypto.randomUUID();
            }
            // Fallback for older browsers / restricted environments
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        };

        // Generate or get persistent host ID for this device
        let storedHostId = null;
        try {
            storedHostId = localStorage.getItem('local_host_id');
            if (!storedHostId) {
                storedHostId = generateUUID();
                localStorage.setItem('local_host_id', storedHostId);
            }
        } catch (e) {
            console.warn("Storage access denied, using temporary session ID", e);
            storedHostId = generateUUID();
        }

        setHostId(storedHostId);
        fetchRoster(storedHostId);
    }, []);

    const fetchRoster = async (hid) => {
        try {
            const res = await axios.get(`${API}/roster/${hid}`);
            setRoster(res.data.names);
        } catch (err) {
            console.error("Error fetching roster", err);
            toast.error("Error al cargar lista de jugadores");
        }
    };

    const handleAddName = async () => {
        const name = newName.trim();
        if (!name) return;
        if (roster.includes(name)) {
            toast.error("Este nombre ya existe");
            return;
        }

        try {
            await axios.post(`${API}/roster/${hostId}`, { name });
            setRoster([...roster, name]);
            setNewName("");
            // Auto-select the new player
            setSelectedPlayers(prev => [...prev, name]);
        } catch (err) {
            toast.error("Error al guardar nombre");
        }
    };

    const handleRemoveName = async (name) => {
        try {
            await axios.delete(`${API}/roster/${hostId}/${name}`);
            setRoster(roster.filter(n => n !== name));
            setSelectedPlayers(selectedPlayers.filter(n => n !== name));
        } catch (err) {
            toast.error("Error al eliminar nombre");
        }
    };

    const getRoleDistribution = (playerCount) => {
        // En Modo Director, un jugador sale del pool de roles
        const effectiveCount = directorMode ? playerCount - 1 : playerCount;

        if (overrideRoles) {
            // Calculate agents based on custom values
            const agents = Math.max(0, effectiveCount - customInfiltrators - customImpostor);
            return { agents, infiltrators: customInfiltrators, impostor: customImpostor };
        }

        if (effectiveCount <= 0) return { agents: 0, infiltrators: 0, impostor: 0 };

        // Logic for small counts
        if (effectiveCount < 3) {
            return { agents: Math.max(0, effectiveCount - 1), infiltrators: 1, impostor: 0 };
        }

        const distributions = {
            3: { agents: 2, infiltrators: 1, impostor: 0 },
            4: { agents: 2, infiltrators: 1, impostor: 1 },
            5: { agents: 3, infiltrators: 1, impostor: 1 },
            6: { agents: 3, infiltrators: 2, impostor: 1 },
            7: { agents: 4, infiltrators: 2, impostor: 1 },
            8: { agents: 4, infiltrators: 3, impostor: 1 },
            9: { agents: 5, infiltrators: 3, impostor: 1 },
            10: { agents: 6, infiltrators: 3, impostor: 1 },
            11: { agents: 7, infiltrators: 3, impostor: 1 },
            12: { agents: 7, infiltrators: 4, impostor: 1 },
            13: { agents: 8, infiltrators: 4, impostor: 1 },
            14: { agents: 8, infiltrators: 5, impostor: 1 },
            15: { agents: 9, infiltrators: 5, impostor: 1 },
            16: { agents: 10, infiltrators: 5, impostor: 1 },
            17: { agents: 10, infiltrators: 6, impostor: 1 },
            18: { agents: 11, infiltrators: 6, impostor: 1 },
            19: { agents: 11, infiltrators: 7, impostor: 1 },
            20: { agents: 12, infiltrators: 7, impostor: 1 },
        };
        // Fallback for > 20
        if (effectiveCount > 20) return distributions[20];

        return distributions[effectiveCount] || { agents: effectiveCount, infiltrators: 0, impostor: 0 };
    };

    // Auto-update custom roles when playerCount or mode changes if not overridden
    useEffect(() => {
        if (!overrideRoles && selectedPlayers.length >= 3) {
            // Recalculate based on current mode
            const dist = getRoleDistribution(selectedPlayers.length);
            // Ensure we don't set invalid values if something goes wrong
            setCustomInfiltrators(Math.max(0, dist.infiltrators));
            setCustomImpostor(Math.max(0, dist.impostor));
        }
    }, [selectedPlayers.length, overrideRoles, directorMode]);

    const toggleSelection = (name) => {
        if (selectedPlayers.includes(name)) {
            setSelectedPlayers(selectedPlayers.filter(n => n !== name));
        } else {
            setSelectedPlayers([...selectedPlayers, name]);
        }
    };

    const handleStartGame = async () => {
        if (selectedPlayers.length < 3) {
            toast.error("Necesitas al menos 3 jugadores para empezar");
            return;
        }

        const dist = getRoleDistribution(selectedPlayers.length);
        if (dist.infiltrators + dist.impostor === 0) {
            toast.error("La partida debe tener al menos un Infiltrado o un Impostor");
            return;
        }

        setLoading(true);
        try {
            const res = await axios.post(`${API}/rooms/create_local`, {
                host_id: hostId,
                player_names: selectedPlayers,
                starting_player_assistance: startingAssistance,
                override_roles: overrideRoles,
                infiltrators_count: customInfiltrators,
                impostor_count: customImpostor,
                director_mode: directorMode,
                enable_scoring: enableScoring
            });

            const { room_code, host_player_id } = res.data;

            // Save local session data
            try {
                localStorage.setItem(`room_${room_code}`, host_player_id);
                localStorage.setItem(`player_name`, "El Dispositivo");
            } catch (e) {
                console.warn("Could not save local session to storage", e);
            }

            navigate(`/game/${room_code}?local=true`);
        } catch (err) {
            console.error(err);
            toast.error("Error al iniciar partida local");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen scanlines relative overflow-hidden bg-[#0a0a0b] text-white p-4">
            <div
                className="absolute inset-0 opacity-10"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'grayscale(100%)'
                }}
            />

            <div className="relative z-10 max-w-2xl mx-auto space-y-6">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                        <ArrowLeft className="w-6 h-6" />
                    </Button>
                    <h1 className="font-display text-4xl font-black text-[hsl(var(--electric-cyan))] drop-shadow-[0_0_10px_rgba(0,255,255,0.4)]">
                        MODO LOCAL
                    </h1>
                </div>

                <div className="glass bg-black/40 rounded-xl p-6 space-y-8">
                    {/* Sección: Añadir */}
                    <div className="space-y-4">
                        <h2 className="font-heading text-lg flex items-center gap-2 text-[hsl(var(--cyber-yellow))]">
                            <UserPlus className="w-5 h-5" />
                            AÑADIR JUGADORES
                        </h2>
                        <div className="flex gap-2">
                            <Input
                                placeholder="Nombre del jugador..."
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleAddName()}
                                className="bg-[hsl(var(--charcoal))] border-[hsl(var(--smoke))]"
                            />
                            <Button onClick={handleAddName} className="bg-[hsl(var(--electric-cyan))] text-black hover:bg-[hsl(var(--electric-cyan))]/90">
                                <Plus className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>

                    {/* Sección: Roster */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h2 className="font-heading text-lg flex items-center gap-2 text-[hsl(var(--hot-pink))]">
                                <Users className="w-5 h-5" />
                                PARTICIPANTES: ({selectedPlayers.length} SELECCIONADOS)
                            </h2>
                            <span className="text-xs text-[hsl(var(--text-muted))]">Min. 3</span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
                            {roster.length === 0 ? (
                                <div className="col-span-full text-center py-8 text-[hsl(var(--text-muted))] italic border border-dashed border-[hsl(var(--smoke))] rounded-lg">
                                    No hay jugadores guardados. Añade algunos arriba.
                                </div>
                            ) : (
                                roster.map((name) => (
                                    <div
                                        key={name}
                                        className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${selectedPlayers.includes(name)
                                            ? "bg-[hsl(var(--electric-cyan))]/10 border-[hsl(var(--electric-cyan))] shadow-[0_0_10px_rgba(0,255,255,0.1)]"
                                            : "bg-black/20 border-[hsl(var(--smoke))] hover:bg-white/5"
                                            }`}
                                        onClick={() => toggleSelection(name)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-5 h-5 rounded flex items-center justify-center border ${selectedPlayers.includes(name) ? "bg-[hsl(var(--electric-cyan))] border-[hsl(var(--electric-cyan))]" : "border-[hsl(var(--text-muted))]"
                                                }`}>
                                                {selectedPlayers.includes(name) && <UserCheck className="w-3.5 h-3.5 text-black" />}
                                            </div>
                                            <span className={`font-medium ${selectedPlayers.includes(name) ? "text-white" : "text-[hsl(var(--text-secondary))]"}`}>
                                                {name}
                                            </span>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 hover:text-red-500 hover:bg-red-500/10"
                                            onClick={(e) => { e.stopPropagation(); handleRemoveName(name); }}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Sección: Ajustes */}
                    <div className="space-y-4">
                        <h2 className="font-heading text-lg flex items-center gap-2 text-[hsl(var(--electric-cyan))]">
                            <Sparkles className="w-5 h-5" />
                            AJUSTES DE PARTIDA
                        </h2>
                        <div className="flex flex-col gap-4 p-4 bg-black/50 border border-[hsl(var(--smoke))] rounded-xl">
                            <div className="flex items-center justify-between">
                                <Label className="font-heading text-sm">Asistencia Inicio (Resaltar jugador inicial)</Label>
                                <Switch checked={startingAssistance} onCheckedChange={setStartingAssistance} />
                            </div>

                            <div className="flex items-center justify-between">
                                <Label className="font-heading text-sm text-[hsl(var(--hot-pink))]">Modo Director</Label>
                                <Switch checked={directorMode} onCheckedChange={setDirectorMode} />
                            </div>
                            {directorMode && (
                                <p className="text-[10px] text-[hsl(var(--text-muted))] -mt-3 mb-2">
                                    Un jugador extra será "El Director" y elegirá las palabras cada ronda.
                                </p>
                            )}
                            <div className="flex items-center justify-between">
                                <Label className="font-heading text-sm text-[hsl(var(--cyber-yellow))]">Puntuar partidas</Label>
                                <Switch checked={enableScoring} onCheckedChange={setEnableScoring} />
                            </div>
                        </div>

                        {/* SECCIÓN: Distribución de Roles */}
                        {selectedPlayers.length >= 3 && (() => {
                            const dist = getRoleDistribution(selectedPlayers.length);
                            return (
                                <div className="space-y-3 pt-2">
                                    <div className="flex items-center justify-between px-2 mb-1">
                                        <h4 className="font-heading text-[10px] text-[hsl(var(--text-muted))] uppercase tracking-[0.2em]">Distribución de Roles</h4>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] text-[hsl(var(--text-muted))] uppercase">Personalizar</span>
                                            <Switch checked={overrideRoles} onCheckedChange={setOverrideRoles} className="scale-75 origin-right" />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-2">
                                        {/* AGENTES */}
                                        <div className="bg-black/40 border border-[hsl(var(--smoke))] p-3 rounded-xl flex flex-col items-center transition-all">
                                            <span className="text-[10px] text-[hsl(var(--text-muted))] uppercase font-bold">Agentes</span>
                                            <span className="text-2xl font-black text-[hsl(var(--cyber-yellow))] drop-shadow-[0_0_8px_rgba(255,220,90,0.3)]">{dist.agents}</span>
                                        </div>

                                        {/* INFILTRADOS */}
                                        <div className={`bg-black/40 border p-3 rounded-xl flex flex-col items-center transition-all ${overrideRoles ? 'border-[hsl(var(--hot-pink))] shadow-[0_0_10px_rgba(255,105,180,0.2)]' : 'border-[hsl(var(--smoke))]'}`}>
                                            <span className="text-[10px] text-[hsl(var(--text-muted))] uppercase font-bold">Infiltrados</span>
                                            <div className="flex items-center gap-2">
                                                {overrideRoles && (
                                                    <button
                                                        onClick={() => setCustomInfiltrators(Math.max(0, customInfiltrators - 1))}
                                                        className="w-6 h-6 rounded-full bg-[hsl(var(--hot-pink))]/10 text-[hsl(var(--hot-pink))] flex items-center justify-center hover:bg-[hsl(var(--hot-pink))]/20 active:scale-90 transition-all font-bold"
                                                    >-</button>
                                                )}
                                                <span className="text-2xl font-black text-[hsl(var(--hot-pink))] drop-shadow-[0_0_8px_rgba(255,105,180,0.3)]">{dist.infiltrators}</span>
                                                {overrideRoles && (
                                                    <button
                                                        onClick={() => {
                                                            const poolSize = directorMode ? selectedPlayers.length - 1 : selectedPlayers.length;
                                                            if (poolSize - customImpostor >= 2 * customInfiltrators + 2) {
                                                                setCustomInfiltrators(customInfiltrators + 1);
                                                            } else {
                                                                toast.error("No puede haber más infiltrados que agentes");
                                                            }
                                                        }}
                                                        className="w-6 h-6 rounded-full bg-[hsl(var(--hot-pink))]/10 text-[hsl(var(--hot-pink))] flex items-center justify-center hover:bg-[hsl(var(--hot-pink))]/20 active:scale-90 transition-all font-bold"
                                                    >+</button>
                                                )}
                                            </div>
                                        </div>

                                        {/* IMPOSTORES */}
                                        <div className={`bg-black/40 border p-3 rounded-xl flex flex-col items-center transition-all ${overrideRoles ? 'border-[hsl(var(--electric-cyan))] shadow-[0_0_10px_rgba(0,255,255,0.2)]' : 'border-[hsl(var(--smoke))]'}`}>
                                            <span className="text-[10px] text-[hsl(var(--text-muted))] uppercase font-bold">Impostores</span>
                                            <div className="flex items-center gap-2">
                                                {overrideRoles && (
                                                    <button
                                                        onClick={() => setCustomImpostor(0)}
                                                        className={`w-6 h-6 rounded-full flex items-center justify-center transition-all font-bold ${customImpostor === 0 ? 'bg-[hsl(var(--electric-cyan))] text-black' : 'bg-[hsl(var(--electric-cyan))]/10 text-[hsl(var(--electric-cyan))]'}`}
                                                    >0</button>
                                                )}
                                                {overrideRoles && (
                                                    <button
                                                        onClick={() => {
                                                            const poolSize = directorMode ? selectedPlayers.length - 1 : selectedPlayers.length;
                                                            if (poolSize - customInfiltrators - 1 >= customInfiltrators) {
                                                                setCustomImpostor(1);
                                                            } else {
                                                                toast.error("No hay suficientes agentes para añadir un impostor");
                                                            }
                                                        }}
                                                        className={`w-6 h-6 rounded-full flex items-center justify-center transition-all font-bold ${customImpostor === 1 ? 'bg-[hsl(var(--electric-cyan))] text-black' : 'bg-[hsl(var(--electric-cyan))]/10 text-[hsl(var(--electric-cyan))]'}`}
                                                    >1</button>
                                                )}
                                                {!overrideRoles && (
                                                    <span className="text-2xl font-black text-[hsl(var(--electric-cyan))] drop-shadow-[0_0_8px_rgba(0,255,255,0.3)]">{dist.impostor}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-center text-[10px] text-[hsl(var(--text-muted))] italic">
                                        Total: {selectedPlayers.length} jugadores
                                    </p>
                                </div>
                            );
                        })()}
                    </div>

                    {/* Botón Comienzo */}
                    <Button
                        className="w-full h-14 text-lg font-heading font-bold bg-[hsl(var(--cyber-yellow))] text-black hover:bg-[hsl(var(--cyber-yellow))]/90 shadow-[0_0_20px_rgba(255,220,90,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleStartGame}
                        disabled={selectedPlayers.length < 3 || loading}
                    >
                        {loading ? "PREPARANDO..." : "COMENZAR PARTIDA"}
                        <Play className="ml-2 w-5 h-5 fill-current" />
                    </Button>
                </div>
            </div>
        </div>
    );
}
